// src/components/Pages/ManageCalendarPage.js
import React, { useState, useEffect } from 'react';
import { notifications } from '@mantine/notifications';
import {
    Box,
    Container,
    Title,
    Text,
    useMantineTheme,
    useMantineColorScheme,
    Paper,
    Loader,
    Group,
    Alert,
    Modal,
    SimpleGrid,
    Badge,
    Stack,
    Card,
    Button,
    Switch,
} from '@mantine/core';
import { IconColumns, IconAlertCircle, IconWorld, IconPencil, IconSend, IconDownload, IconCircleCheck, IconCloudUpload } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { getFirestore, collection, query, where, doc, updateDoc, onSnapshot } from 'firebase/firestore';

import { useBackgroundImage } from '../../context/BackgroundImageContext';
import { useAuth } from '../../context/AuthContext';
import backgroundImageFile from '../../assets/images/brochure-background.jpg';
import BriefDisplay from '../Briefs/BriefDisplay';

// This map defines the columns for our Kanban board.
// The order here is the order they will appear on the screen.
const STATUS_COLUMNS = {
    'Pending': { title: 'Pending Briefs', icon: <IconPencil size={18} /> },
    'Dispatched': { title: 'Dispatched to Writer', icon: <IconSend size={18} /> },
    'Draft Received': { title: 'Draft Received', icon: <IconDownload size={18} /> },
    'Draft Approved': { title: 'Draft Approved', icon: <IconCircleCheck size={18} /> },
    'Uploaded to CMS': { title: 'Uploaded to CMS', icon: <IconCloudUpload size={18} /> },
};

// Reusing the brand colors from your other component
const brandColorMap = {
    'LoveMoney': 'rgb(174, 26, 18)',
    'LoveProperty': 'rgb(82, 110, 167)',
    'LoveFood': 'rgb(223, 82, 135)',
    'LoveExploring': 'rgb(5, 146, 159)',
};

// A helper function to format deadlines, similar to your existing one.
const formatDeadline = (deadline) => {
    if (!deadline) return 'No deadline';
    const d = deadline.toDate ? deadline.toDate() : new Date(deadline);
    if (isNaN(d.getTime())) return 'Invalid Date';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

// We've created a dedicated component for the brief cards to keep the main component cleaner.
function BriefCard({ brief, index, onOpenModal, viewMode }) {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';

    const getBrandColor = (brand) => brandColorMap[brand] || 'rgb(122, 59, 91)';

    return (
        <Draggable draggableId={brief.id} index={index}>
            {(provided, snapshot) => (
                <Card
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    withBorder
                    radius="md"
                    p="sm"
                    mb="sm"
                    shadow={snapshot.isDragging ? 'xl' : 'sm'}
                    style={{
                        ...provided.draggableProps.style,
                        backgroundColor: isDark ? theme.colors.dark[6] : theme.white,
                        // --- UPDATED: Conditionally set cursor based on viewMode ---
                        cursor: viewMode === 'team' ? 'default' : (snapshot.isDragging ? 'grabbing' : 'grab'),
                    }}
                >
                    <Stack gap="xs">
                        <Text fw={500} size="sm">
                            {brief.generatedBrief?.titleSuggestions?.writerHubAlternatives[0] || 'Untitled Brief'}
                        </Text>
                        <Text size="xs" c="dimmed" fs="italic">
                            Due: {formatDeadline(brief.deadline)} |  {brief.assignedWriter || 'N/A'}
                        </Text>
                        <Group gap="xs" mt="xs">
                            {brief.requestInputs?.target_brand && (
                                <Badge color={getBrandColor(brief.requestInputs.target_brand)} variant="filled" size="xs">{brief.requestInputs.target_brand}</Badge>
                            )}
                            {brief.requestInputs?.target_region && (
                                <Badge variant="light" color="gray" size="xs" leftSection={<IconWorld size={12} />}>
                                    {brief.requestInputs.target_region}
                                </Badge>
                            )}
                        </Group>
                        <Button
                            variant="light"
                            size="xs"
                            mt="xs"
                            onClick={() => onOpenModal(brief.generatedBrief)}
                        >
                            View Brief
                        </Button>
                    </Stack>
                </Card>
            )}
        </Draggable>
    );
}


function ManageCalendarPage() {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const { isBackgroundImageVisible } = useBackgroundImage();
    const { currentUser, userProfile } = useAuth();
    const headerTextColor = isBackgroundImageVisible ? theme.white : undefined;

    const [briefs, setBriefs] = useState({}); // We'll store briefs categorized by status
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
    const [selectedBriefForModal, setSelectedBriefForModal] = useState(null);
    const [viewMode, setViewMode] = useState('personal');

    // This effect will fetch all the necessary briefs from Firestore.
    // We use onSnapshot to listen for real-time updates.
    // This effect will fetch the necessary briefs from Firestore.
    // By default, it only fetches briefs for the current user.
    // This effect now dynamically fetches briefs based on the selected view mode.
    useEffect(() => {
        if (!currentUser || !userProfile) { // Also wait for userProfile to be loaded
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        const db = getFirestore();
        const briefsCollection = collection(db, 'generated_briefs');
        const statuses = Object.keys(STATUS_COLUMNS);

        let q; // Declare query variable

        // --- NEW: Dynamic Query Logic ---
        if (viewMode === 'team' && (userProfile.role === 'admin' || userProfile.role === 'management')) {
            // Team view: Fetch all briefs with a relevant status
            console.log("Fetching TEAM briefs...");
            q = query(
                briefsCollection,
                where('status', 'in', statuses)
            );
        } else {
            // Personal view (default for everyone)
            console.log("Fetching PERSONAL briefs...");
            q = query(
                briefsCollection,
                where('status', 'in', statuses),
                where('uid', '==', currentUser.uid)
            );
        }

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const categorizedBriefs = {};
            statuses.forEach(status => categorizedBriefs[status] = []);

            querySnapshot.forEach(doc => {
                const briefData = { id: doc.id, ...doc.data() };

                if (briefData.isArchived !== true) {
                    if (categorizedBriefs[briefData.status]) {
                        categorizedBriefs[briefData.status].push(briefData);
                    }
                }
            });

            // Sorting logic remains the same
            for (const status in categorizedBriefs) {
                categorizedBriefs[status].sort((a, b) => {
                    const timeA = a.deadline ? new Date(a.deadline.toDate ? a.deadline.toDate() : a.deadline).getTime() : 0;
                    const timeB = b.deadline ? new Date(b.deadline.toDate ? b.deadline.toDate() : b.deadline).getTime() : 0;
                    return timeA - timeB;
                });
            }

            setBriefs(categorizedBriefs);
            setIsLoading(false);
        }, (err) => {
            console.error("Error fetching briefs for Kanban:", err);
            setError("Failed to fetch briefs. A new Firestore index might be required for the current view. Please check the console for a link.");
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser, userProfile, viewMode]); // <<< UPDATED: Re-run when the viewMode or user changes

    // This function handles the logic when you stop dragging a card.
    const handleOnDragEnd = async (result) => {
        // --- UPDATED: If in team view, show notification and do not allow dragging ---
        if (viewMode === 'team') {
            notifications.show({
                title: 'Team View is Read-Only!',
                message: 'Brief Owners can change their own items separately.',
                color: 'blue',
                autoClose: 3000,
            });
            return; // Stop the function here
        }

        const { source, destination, draggableId } = result;

        // Do nothing if dropped outside a droppable area
        if (!destination) return;

        // Do nothing if dropped in the same place
        if (source.droppableId === destination.droppableId && source.index === destination.index) {
            return;
        }

        const startColumnStatus = source.droppableId;
        const endColumnStatus = destination.droppableId;

        const startColumnBriefs = Array.from(briefs[startColumnStatus]);
        const [movedBrief] = startColumnBriefs.splice(source.index, 1);

        // Optimistically update the UI state
        const newBriefsState = { ...briefs };
        newBriefsState[startColumnStatus] = startColumnBriefs;

        if (startColumnStatus === endColumnStatus) {
            // Reordering within the same column
            startColumnBriefs.splice(destination.index, 0, movedBrief);
        } else {
            // Moving to a different column
            const endColumnBriefs = Array.from(briefs[endColumnStatus]);
            endColumnBriefs.splice(destination.index, 0, movedBrief);
            newBriefsState[endColumnStatus] = endColumnBriefs;
        }

        setBriefs(newBriefsState);

        // Update the document's status in Firestore
        try {
            const db = getFirestore();
            const briefRef = doc(db, 'generated_briefs', draggableId);
            await updateDoc(briefRef, {
                status: endColumnStatus
            });
            console.log(`Brief ${draggableId} status updated to ${endColumnStatus}`);
        } catch (err) {
            console.error("Failed to update brief status:", err);
            // If the update fails, revert the UI state to show an error
            // (For simplicity, we're not reverting here, but in a production app you might)
            setError("Failed to update brief status. Please try again.");
        }
    };

    const handleOpenModal = (brief) => {
        setSelectedBriefForModal(brief);
        openModal();
    };

    const renderContent = () => {
        if (isLoading) { return (<Group justify="center" mt="xl"><Loader /><Text>Loading Kanban Board...</Text></Group>); }
        if (error) { return (<Alert title="Error" color="red" icon={<IconAlertCircle />}>{error}</Alert>); }

        return (
            <DragDropContext onDragEnd={handleOnDragEnd}>
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 5 }} spacing="md">
                    {Object.entries(STATUS_COLUMNS).map(([status, columnInfo]) => (
                        <Droppable droppableId={status} key={status}>
                            {(provided, snapshot) => (
                                <Paper
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    withBorder
                                    p="md"
                                    radius="md"
                                    style={{
                                        backgroundColor: snapshot.isDraggingOver
                                            ? (isDark ? theme.colors.dark[8] : theme.colors.gray[1])
                                            : (isDark ? theme.colors.dark[7] : theme.colors.gray[0]),
                                        minHeight: '400px',
                                        transition: 'background-color 0.2s ease',
                                    }}
                                >
                                    <Group justify="space-between" mb="md">
                                        <Group gap="xs">
                                            {columnInfo.icon}
                                            <Title order={5}>{columnInfo.title}</Title>
                                        </Group>
                                        <Badge variant="light">{briefs[status]?.length || 0}</Badge>
                                    </Group>
                                    <Box style={{ height: 'calc(100% - 40px)', overflowY: 'auto' }}>
                                        {briefs[status]?.map((brief, index) => (
                                            <BriefCard
                                                key={brief.id}
                                                brief={brief}
                                                index={index}
                                                onOpenModal={handleOpenModal}
                                                viewMode={viewMode}
                                            />
                                        ))}
                                        {provided.placeholder}
                                    </Box>
                                </Paper>
                            )}
                        </Droppable>
                    ))}
                </SimpleGrid>
            </DragDropContext>
        );
    };

    return (
        <Box
            style={{
                width: '100%',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                backgroundImage: isBackgroundImageVisible ? `url(${backgroundImageFile})` : 'none',
                backgroundColor: !isBackgroundImageVisible ? (isDark ? theme.colors.dark[7] : theme.colors.gray[0]) : 'transparent',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                overflow: 'auto',
            }}
        >
            <Container fluid px="xl" py="xl" style={{ flexGrow: 1 }}>
                <Title order={1} ta="center" mb="xs" style={{ color: headerTextColor }}>
                    <IconColumns color={theme.colors.yellow[isDark || isBackgroundImageVisible ? 4 : 6]} style={{ marginRight: theme.spacing.sm, verticalAlign: 'middle' }} />
                    New Article Pipeline
                </Title>
                <Text size="xl" ta="center" mb="xl" style={{ color: headerTextColor }}>
                    Drag and drop briefs to update their status in the content pipeline.
                </Text>
                {userProfile && (userProfile.role === 'admin' || userProfile.role === 'management') && (
                    <Group justify="center" mb="xl">
                        {/* <text style={{ color: headerTextColor }}>My View</text> */}
                        <Switch
                            label="Team View"
                            style={{ color: headerTextColor }}
                            //color="dimmed"
                            checked={viewMode === 'team'}
                            onChange={(event) => setViewMode(event.currentTarget.checked ? 'team' : 'personal')}
                            size="md"
                        />
                        
                        {/* 
                        <Text size="md" style={{ color: headerTextColor, fontStyle: 'italic' }}>
                            {viewMode === 'team' ? '[On. Read-only, viewing all team briefs.]' : '[Off. Manage your personal briefs.]'}
                        </Text>
                        */}
                    </Group>
                )}

                {renderContent()}
            </Container>
            <Modal opened={modalOpened} onClose={closeModal} title="Brief Details" size="xl" scrollAreaComponent={Modal.NativeScrollArea}>
                {selectedBriefForModal && <BriefDisplay brief={selectedBriefForModal} />}
            </Modal>
        </Box>
    );
}

export default ManageCalendarPage;